import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PackageCard = ({ package: pkg, onEdit, onDelete, onToggleStatus }) => {
  const formatAmount = (value, currencyCode) => {
    return value ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currencyCode || 'EUR' }).format(value) : '-';
  };

  const getTierConfig = (tier) => {
    const configs = {
      bronze: {
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        icon: 'Medal',
        label: 'Bronze'
      },
      silver: {
        color: 'text-gray-600',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-300',
        icon: 'Award',
        label: 'Silver'
      },
      gold: {
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-300',
        icon: 'Crown',
        label: 'Gold'
      }
    };
    return configs?.[tier] || configs?.silver;
  };

  const tierConfig = getTierConfig(pkg?.tier);

  return (
    <div className="bg-card rounded-lg border border-border shadow-card hover:shadow-interactive transition-all duration-200">
      <div className={`p-6 border-b ${tierConfig?.borderColor} ${tierConfig?.bgColor}`}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-foreground mb-1">{pkg?.packageName}</h3>
            <p className="text-sm text-muted-foreground">{pkg?.packageCode}</p>
            {pkg?.country && (
              <div className="flex items-center gap-1 mt-2">
                <Icon name="MapPin" size={14} className="text-primary" />
                <span className="text-xs text-primary font-medium">
                  {pkg?.country?.countryName} ({pkg?.country?.countryCode})
                </span>
              </div>
            )}
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            pkg?.status === 'active' ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
          }`}>
            {pkg?.status === 'active' ? 'Actif' : pkg?.status === 'inactive' ? 'Inactif' : 'Archivé'}
          </span>
        </div>
        <p className="text-sm text-muted-foreground">{pkg?.description}</p>
      </div>

      <div className="p-6">
        <div className="mb-4">
          <div className="flex items-baseline gap-2 mb-2">
            {pkg?.discountPercentage > 0 && (
              <span className="text-lg line-through text-muted-foreground">{formatAmount(pkg?.basePrice,pkg?.country?.currencyCode)}</span>
            )}
            <span className="text-3xl font-bold text-foreground">{formatAmount(pkg?.finalPrice,pkg?.country?.currencyCode)}</span>
            {pkg?.discountPercentage > 0 && (
              <span className="px-2 py-1 bg-success/10 text-success text-xs font-semibold rounded">
                -{pkg?.discountPercentage}%
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Package" size={14} />
            <span>{pkg?.includedUnits} unités incluses</span>
          </div>
          {pkg?.additionalUnitPrice && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
              <Icon name="Plus" size={14} />
              <span>{formatAmount(pkg?.additionalUnitPrice,pkg?.country?.currencyCode)} par unité supplémentaire</span>
            </div>
          )}
        </div>

        <div className="mb-4">
          <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
            <Icon name="List" size={14} />
            Services inclus:
          </h4>
          {pkg?.services && pkg?.services?.length > 0 ? (
            <ul className="space-y-1.5">
              {pkg?.services?.slice(0, 4)?.map((service) => (
                <li key={service?.id} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <span className="font-medium text-foreground">{service?.serviceName}</span>
                    <span className="text-xs ml-2">({service?.quantity}x)</span>
                  </div>
                </li>
              ))}
              {pkg?.services?.length > 4 && (
                <li className="text-sm text-primary font-medium ml-6">
                  +{pkg?.services?.length - 4} autres services
                </li>
              )}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground italic">Aucun service associé</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-muted/30 rounded-lg">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Ventes totales</p>
            <p className="text-lg font-semibold text-foreground">{pkg?.totalSales || 0}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Revenus générés</p>
            <p className="text-lg font-semibold text-foreground">{formatAmount(pkg?.revenueGenerated)}</p>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            fullWidth
            iconName="Edit"
            onClick={() => onEdit?.(pkg)}
          >
            Modifier
          </Button>
          <Button
            variant={pkg?.status === 'active' ? 'outline' : 'default'}
            size="sm"
            fullWidth
            iconName={pkg?.status === 'active' ? 'EyeOff' : 'Eye'}
            onClick={() => onToggleStatus?.(pkg)}
          >
            {pkg?.status === 'active' ? 'Désactiver' : 'Activer'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Trash2"
            onClick={() => onDelete?.(pkg)}
            className="text-error hover:text-error hover:border-error"
          />
        </div>
      </div>
    </div>
  );
};

export default PackageCard;